import React, { useEffect, useState } from "react";
import { BarChart3, RefreshCw, ShieldCheck, TrendingDown } from "lucide-react";
import { useAuth } from "../context/AuthContext";
import { fetchAnalyticsSummary, fetchAnalyticsFunnel } from "../lib/analytics";
import PostHogReporting from "../components/PostHogReporting";
import AdminHealthTile from "../components/AdminHealthTile";
import AdminShell from "../components/AdminShell";
import FirstPartyAnalyticsFunnel from "../components/AdminFirstPartyFunnel";
import AdminFounderSummary from "../components/AdminFounderSummary";

/**
 * AdminAnalytics — /admin/analytics
 *
 * Dark "ops console" theme (same as the rest of /admin/*). Funnel,
 * retention signals, event counts, and cohort split for the chosen
 * window (7 / 30 / 90 days).
 */

const FUNNEL_COLORS = [
  "#D4B483",
  "#C4A373",
  "#B49363",
  "#A48353",
  "#9B6A6C",
  "#8B5A5C",
  "#7B4A4C",
  "#6B3A3C",
  "#5B2A2C",
];

const fmt = (n) => (n || 0).toLocaleString();
const fmtDate = (value) => {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "—";
  return date.toLocaleDateString(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  });
};

const TRIAL_OUTCOMES = {
  trialing: { label: "Trial active", color: "text-[#D4B483]" },
  cancel_scheduled_before_payment: {
    label: "Cancel scheduled before charge",
    color: "text-[#F4B8C0]",
  },
  canceled_before_payment: {
    label: "Canceled before charge",
    color: "text-[#F4B8C0]",
  },
  converted_to_paid: { label: "Converted to paid", color: "text-[#9FD3B4]" },
  ended_without_payment: { label: "Ended without payment", color: "text-white/55" },
};

const planLabel = (packageId) =>
  packageId === "pro_annual"
    ? "Annual Pro"
    : packageId === "pro_monthly"
      ? "Monthly Pro"
      : packageId || "Unknown";

const FunnelRow = ({ label, users, peak, color, dropoffPct }) => {
  const pct = peak > 0 ? Math.round((users / peak) * 1000) / 10 : 0;
  return (
    <div
      className="space-y-1.5"
      data-testid={`funnel-row-${label.toLowerCase().replace(/\s+/g, "-")}`}
    >
      <div className="flex items-baseline justify-between text-sm">
        <span className="text-white font-medium">{label}</span>
        <span className="text-white/55 tabular-nums">
          {fmt(users)} <span className="text-white/35">· {pct}%</span>
          {dropoffPct !== null && dropoffPct !== undefined && (
            <span className="ml-2 text-xs text-[#F4B8C0]">
              <TrendingDown className="inline h-3 w-3 -mt-0.5" /> {dropoffPct}%
            </span>
          )}
        </span>
      </div>
      <div className="h-3 w-full bg-white/[0.06] rounded-full overflow-hidden">
        <div
          className="h-full rounded-full transition-all duration-500"
          style={{ width: `${pct}%`, background: color }}
        />
      </div>
    </div>
  );
};

const StatCard = ({ label, value, hint }) => (
  <div className="bg-white/[0.04] border border-white/10 rounded-2xl p-5">
    <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
      {label}
    </div>
    <div className="text-3xl font-light text-white mb-0.5 tabular-nums">
      {value}
    </div>
    {hint && <div className="text-xs text-white/40">{hint}</div>}
  </div>
);

const Panel = ({ title, meta, children, testid }) => (
  <div
    className="bg-white/[0.03] border border-white/10 rounded-2xl p-6 sm:p-7 mb-6"
    data-testid={testid}
  >
    <div className="flex items-center justify-between mb-5">
      <h2 className="text-lg font-medium text-white">{title}</h2>
      {meta && (
        <span className="text-[10px] uppercase tracking-[0.22em] text-white/40">
          {meta}
        </span>
      )}
    </div>
    {children}
  </div>
);

export default function AdminAnalytics() {
  const [summary, setSummary] = useState(null);
  const [consultationFunnel, setConsultationFunnel] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [days, setDays] = useState(30);
  const { loading: authLoading } = useAuth();

  const load = async (windowDays = days) => {
    setLoading(true);
    setError(null);
    try {
      const [summaryData, funnelData] = await Promise.all([
        fetchAnalyticsSummary(windowDays),
        // Pull the conversion-funnel card data in parallel — it's a
        // separate endpoint so the founder gets the consultation
        // bottleneck even if the heavy `summary` blob fails.
        fetchAnalyticsFunnel(windowDays).catch(() => null),
      ]);
      setSummary(summaryData);
      setConsultationFunnel(funnelData?.consultation_funnel || null);
    } catch (e) {
      setError(e?.response?.status || "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!authLoading) load(days);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authLoading, days]);

  const peakFunnel =
    summary?.funnel?.reduce((m, f) => Math.max(m, f.users || 0), 0) || 0;

  if (summary?.provider === "posthog") {
    return <AdminShell eyebrow="Analytics" title="TruBeauté analytics">
      <PostHogReporting status={summary} />
    </AdminShell>;
  }

  return (
    <AdminShell
      eyebrow="Analytics"
      title="How TruBeauté is being used"
      subtitle="First-party event funnel · retention signals · cohort split. Window is configurable; numbers refresh on every load."
      actions={
        <>
          {[7, 30, 90].map((d) => (
            <button
              key={d}
              onClick={() => setDays(d)}
              className={`text-xs rounded-full px-3 py-1.5 transition-colors ${
                d === days
                  ? "bg-[#D4B483] text-[#1A1720] font-medium"
                  : "bg-white/[0.06] text-white/70 hover:text-white hover:bg-white/[0.12] border border-white/10"
              }`}
              data-testid={`analytics-window-${d}`}
            >
              {d}d
            </button>
          ))}
          <button
            onClick={() => load(days)}
            className="inline-flex items-center gap-2 bg-white/[0.06] hover:bg-white/[0.12] border border-white/10 text-white rounded-full px-3 py-1.5 transition-all text-xs"
            data-testid="analytics-refresh-btn"
          >
            <RefreshCw className="h-3.5 w-3.5" />
            Refresh
          </button>
        </>
      }
    >
      {loading && !summary && (
        <div className="text-sm text-white/60">Reading analytics…</div>
      )}

      {error && (
        <div className="bg-[#7F2A2A]/20 border border-[#B85F5F]/40 text-[#F4B8C0] rounded-2xl p-5 text-sm mb-6">
          Couldn't load analytics. Please refresh.
        </div>
      )}

      {summary && (
        <>
          {/* "PMF in 30 seconds" headline tile — must be the first thing
              the founder sees on the page, so positioned above even the
              admin health tile + privacy reminder. Window defaults to 7d. */}
          <AdminFounderSummary days={7} />

          <AdminHealthTile className="mb-6" />

          <div
            className="bg-[#D4B483]/10 border border-[#D4B483]/25 rounded-2xl p-4 mb-8 flex items-start gap-3"
            data-testid="analytics-privacy-reminder"
          >
            <ShieldCheck className="h-5 w-5 text-[#D4B483] mt-0.5 shrink-0" />
            <div className="text-sm text-white/80 leading-relaxed">
              <span className="font-medium text-white">
                First-party analytics, stealth-aware.
              </span>{" "}
              No third-party trackers. No session replays. No IP storage.
              The funnel below INCLUDES Stealth Mode users (anonymized,
              with the stealth_mode flag) so we can measure product usage
              without breaking the privacy promise. The legacy panels
              further down still exclude stealth events.
            </div>
          </div>

          {/* New first-party, stealth-aware funnel (Feb 2026 sprint) —
              reads ONLY events tagged source=first_party_backend so it
              never double-counts with the legacy /api/events stream
              rendered below. */}
          <FirstPartyAnalyticsFunnel days={days} />

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
            <StatCard label="Window" value={`${summary.window_days}d`} />
            <StatCard
              label="Total events"
              value={fmt(summary.total_events)}
              hint={`${fmt(summary.counts.length)} unique`}
            />
            <StatCard
              label="Onboarding completion"
              value={
                summary.onboarding_completion_pct != null
                  ? `${summary.onboarding_completion_pct}%`
                  : "—"
              }
              hint="of users who started"
            />
            <StatCard
              label="Routines viewed"
              value={fmt(
                summary.funnel.find((f) => f.event === "routine_viewed")
                  ?.users || 0,
              )}
              hint="distinct users"
            />
          </div>

          {summary.trial_conversion && (
            <Panel
              title="Trial conversion · preselected plan → first charge"
              meta={`Trials started in this ${summary.window_days}d window`}
              testid="analytics-trial-conversion"
            >
              <p className="text-sm text-white/55 mb-5 -mt-3">
                Billing-backed lifecycle data. Shows which Pro plan each member
                selected before starting their trial, the scheduled trial end,
                and whether they canceled before the first paid invoice.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-6">
                <StatCard
                  label="Trials started"
                  value={fmt(summary.trial_conversion.started)}
                />
                <StatCard
                  label="Active trials"
                  value={fmt(summary.trial_conversion.outcomes?.trialing)}
                />
                <StatCard
                  label="Cancel scheduled"
                  value={fmt(
                    summary.trial_conversion.outcomes
                      ?.cancel_scheduled_before_payment,
                  )}
                  hint="before first charge"
                />
                <StatCard
                  label="Canceled"
                  value={fmt(
                    summary.trial_conversion.outcomes?.canceled_before_payment,
                  )}
                  hint="before first charge"
                />
                <StatCard
                  label="Trial conversion"
                  value={
                    summary.trial_conversion.conversion_pct != null
                      ? `${summary.trial_conversion.conversion_pct}%`
                      : "—"
                  }
                  hint="paid / completed trials"
                />
              </div>

              <div className="flex flex-wrap gap-2 mb-5">
                {Object.entries(summary.trial_conversion.plan_counts || {}).map(
                  ([packageId, count]) => (
                    <span
                      key={packageId}
                      className="rounded-full border border-white/10 bg-white/[0.04] px-3 py-1 text-xs text-white/70"
                    >
                      {planLabel(packageId)} · {fmt(count)}
                    </span>
                  ),
                )}
              </div>

              {summary.trial_conversion.rows?.length > 0 ? (
                <div className="overflow-x-auto -mx-2 px-2">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="text-left text-[10px] uppercase tracking-[0.18em] text-white/40">
                        <th className="py-2 pr-4">Member</th>
                        <th className="py-2 pr-4">Preselected plan</th>
                        <th className="py-2 pr-4">Trial ends</th>
                        <th className="py-2 pr-4">Outcome</th>
                        <th className="py-2 text-right">Outcome date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {summary.trial_conversion.rows.map((row) => {
                        const outcome = TRIAL_OUTCOMES[row.outcome] || {
                          label: row.outcome,
                          color: "text-white/60",
                        };
                        return (
                          <tr
                            key={`${row.user_id}-${row.trial_started_at}`}
                            className="border-t border-white/5"
                            data-testid={`analytics-trial-${row.user_id}`}
                          >
                            <td className="py-3 pr-4 font-mono text-xs text-white/65">
                              {row.user_id || "—"}
                            </td>
                            <td className="py-3 pr-4 text-white">
                              {planLabel(row.package_id)}
                            </td>
                            <td className="py-3 pr-4 text-white/75 tabular-nums">
                              {fmtDate(row.trial_ends_at)}
                            </td>
                            <td className={`py-3 pr-4 ${outcome.color}`}>
                              {outcome.label}
                            </td>
                            <td className="py-3 text-right text-white/55 tabular-nums">
                              {fmtDate(row.first_paid_at || row.cancel_requested_at)}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              ) : (
                <div className="py-6 text-center text-sm text-white/50">
                  No trials started in this window yet.
                </div>
              )}
            </Panel>
          )}

          {/* ─── Single-glance Conversion Funnel (launch-readiness Jun 2026)
              Surfaces the LARGEST drop-off in the consultation flow so
              the founder can identify the conversion bottleneck in under
              5 seconds. Rendered at the very top — above the engagement
              funnel that has 8 noisier steps. */}
          {consultationFunnel && consultationFunnel.length > 0 && (
            <Panel
              title="Conversion funnel · homepage → Pro"
              meta="Feb 2026 canonical funnel"
              testid="analytics-consultation-funnel"
            >
              <p className="text-sm text-white/55 mb-3 -mt-3">
                The 10-step canonical funnel. Want filters, source split,
                and the activation card?{" "}
                <a
                  href="/admin/analytics/funnel"
                  className="underline text-[#D4B483] hover:text-white transition-colors"
                  data-testid="admin-analytics-open-funnel-dashboard"
                >
                  Open the funnel dashboard →
                </a>
              </p>
              <div className="space-y-3">
                {consultationFunnel.map((row, i) => {
                  const prev = i > 0 ? consultationFunnel[i - 1].users : null;
                  const peak = consultationFunnel[0]?.users || 0;
                  const widthPct = peak > 0 ? Math.max(4, Math.round(100 * row.users / peak)) : 4;
                  const stepPct = row.step_pct;
                  const isWorstDropoff =
                    stepPct !== null &&
                    stepPct === Math.min(
                      ...consultationFunnel
                        .slice(1)
                        .map((r) => r.step_pct ?? 100)
                    );
                  return (
                    <div
                      key={row.event}
                      className="grid grid-cols-[1.1fr_auto_auto] items-center gap-4"
                      data-testid={`analytics-consult-funnel-${row.event}`}
                    >
                      <div>
                        <div className="text-[11px] uppercase tracking-[0.18em] text-white/60 mb-1">
                          {row.label}
                        </div>
                        <div className="h-2 rounded-full bg-white/[0.07] overflow-hidden">
                          <div
                            className="h-full rounded-full"
                            style={{
                              width: `${widthPct}%`,
                              background: isWorstDropoff
                                ? "linear-gradient(90deg, #E78284 0%, #C46A6C 100%)"
                                : "linear-gradient(90deg, #D4B483 0%, #C49993 100%)",
                            }}
                          />
                        </div>
                      </div>
                      <div className="text-white text-lg font-light tabular-nums min-w-[60px] text-right">
                        {row.users.toLocaleString()}
                      </div>
                      <div
                        className={`text-xs min-w-[60px] text-right ${
                          isWorstDropoff ? "text-[#E78284]" : "text-white/50"
                        }`}
                      >
                        {stepPct === null
                          ? "—"
                          : isWorstDropoff
                          ? `↓ ${100 - stepPct}%`
                          : `${stepPct}%`}
                      </div>
                    </div>
                  );
                })}
              </div>
              {(consultationFunnel[0]?.users || 0) === 0 && (
                <div
                  className="mt-5 px-4 py-3 bg-white/[0.04] border border-white/10 rounded-2xl text-sm text-white/60"
                  data-testid="consult-funnel-empty"
                >
                  No consultation traffic in this window yet — drive a few
                  ads to the homepage and check back.
                </div>
              )}
            </Panel>
          )}

          <Panel
            title="User journey funnel"
            meta="Distinct users"
            testid="analytics-funnel"
          >
            <div className="space-y-4">
              {summary.funnel.map((f, i) => {
                const prev = i > 0 ? summary.funnel[i - 1].users : null;
                const dropoffPct =
                  prev && prev > 0
                    ? Math.round(1000 * (1 - f.users / prev)) / 10
                    : null;
                return (
                  <FunnelRow
                    key={f.event}
                    label={f.label}
                    users={f.users}
                    peak={peakFunnel}
                    color={FUNNEL_COLORS[i] || "#9B6A6C"}
                    dropoffPct={i > 0 ? dropoffPct : null}
                  />
                );
              })}
            </div>
            {peakFunnel === 0 && (
              <div className="text-center py-10 text-sm text-white/50">
                No journey data yet for this window. Send the share link to
                a few testers and check back.
              </div>
            )}
          </Panel>

          {summary.retention_signals && (
            <Panel
              title="Retention signals"
              meta="Silent · admin only"
              testid="analytics-retention-signals"
            >
              <p className="text-sm text-white/50 mb-5 -mt-3">
                The mirror is the "wow" moment. These signals tell you who
                got a routine but never tried it — gold for retention work.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3 mb-6">
                <div className="bg-white/[0.05] rounded-xl border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
                    Mirror engagement
                  </div>
                  <div className="text-3xl font-light text-white tabular-nums">
                    {summary.retention_signals.mirror_engagement_pct != null
                      ? `${summary.retention_signals.mirror_engagement_pct}%`
                      : "—"}
                  </div>
                  <div className="text-xs text-white/40 mt-0.5">
                    {summary.retention_signals.mirror_opened || 0} of{" "}
                    {summary.retention_signals.routine_viewed || 0} viewers
                  </div>
                </div>
                <div className="bg-white/[0.05] rounded-xl border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
                    Lingered ≥ 10min, no mirror
                  </div>
                  <div className="text-3xl font-light text-white tabular-nums">
                    {summary.retention_signals.lingered_no_mirror_10min || 0}
                  </div>
                  <div className="text-xs text-white/40 mt-0.5">
                    Read but didn't try
                  </div>
                </div>
                <div className="bg-white/[0.05] rounded-xl border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
                    Routine views
                  </div>
                  <div className="text-3xl font-light text-white tabular-nums">
                    {summary.retention_signals.routine_viewed || 0}
                  </div>
                </div>
              </div>
              {summary.retention_signals.left_no_mirror_buckets &&
                summary.retention_signals.left_no_mirror_buckets.length >
                  0 && (
                  <div>
                    <div className="text-[10px] uppercase tracking-[0.22em] text-white/40 mb-3">
                      Left without opening mirror — by dwell time
                    </div>
                    <div className="space-y-2">
                      {summary.retention_signals.left_no_mirror_buckets.map(
                        (b) => {
                          const total =
                            summary.retention_signals.left_no_mirror_buckets.reduce(
                              (s, x) => s + x.count,
                              0,
                            ) || 1;
                          const pct =
                            Math.round((b.count / total) * 1000) / 10;
                          return (
                            <div
                              key={b.label}
                              className="flex items-center gap-3 text-sm"
                            >
                              <span className="w-44 shrink-0 text-white/70">
                                {b.label}
                              </span>
                              <div className="flex-1 h-2 bg-white/[0.06] rounded-full overflow-hidden">
                                <div
                                  className="h-full rounded-full bg-gradient-to-r from-[#D4B483] to-[#9B6A6C] transition-all duration-500"
                                  style={{ width: `${pct}%` }}
                                />
                              </div>
                              <span className="text-white tabular-nums shrink-0 w-16 text-right">
                                {b.count}
                              </span>
                            </div>
                          );
                        },
                      )}
                    </div>
                  </div>
                )}
            </Panel>
          )}

          {summary.coach_card_quadrants &&
            summary.coach_card_quadrants.total_moves > 0 && (
              <Panel
                title="AR drag-mom card — where users park it"
                meta={`${summary.coach_card_quadrants.distinct_users} mover${summary.coach_card_quadrants.distinct_users === 1 ? "" : "s"}`}
                testid="analytics-coach-card-quadrants"
              >
                <p className="text-sm text-white/50 mb-5 -mt-3">
                  Every drag of the AR mirror's drag-mom card is logged with its
                  final quadrant. If most users move it to the same place,
                  ship that as the new default.
                </p>
                <div className="grid grid-cols-2 gap-3 mb-5 max-w-md">
                  {["top-left", "top-right", "bottom-left", "bottom-right"].map(
                    (q) => {
                      const row =
                        summary.coach_card_quadrants.quadrants.find(
                          (x) => x.quadrant === q,
                        ) || { count: 0, pct: 0 };
                      const maxCount = Math.max(
                        ...summary.coach_card_quadrants.quadrants.map(
                          (x) => x.count,
                        ),
                        0,
                      );
                      const isHottest = row.count > 0 && row.count === maxCount;
                      return (
                        <div
                          key={q}
                          className={`rounded-xl border p-4 transition-all ${
                            isHottest
                              ? "bg-[#D4B483]/15 border-[#D4B483]/40"
                              : "bg-white/[0.04] border-white/10"
                          }`}
                          data-testid={`quadrant-${q}`}
                        >
                          <div className="text-[10px] uppercase tracking-[0.22em] text-white/55 mb-1.5">
                            {q.replace("-", " ")}
                          </div>
                          <div
                            className={`text-3xl font-light tabular-nums ${
                              isHottest ? "text-[#D4B483]" : "text-white"
                            }`}
                          >
                            {row.pct}%
                          </div>
                          <div className="text-xs text-white/40 mt-0.5">
                            {row.count} drag{row.count === 1 ? "" : "s"}
                          </div>
                        </div>
                      );
                    },
                  )}
                </div>
                <p className="text-xs text-white/45">
                  Total drags recorded:{" "}
                  <span className="text-white/75 tabular-nums">
                    {summary.coach_card_quadrants.total_moves}
                  </span>{" "}
                  · Default position currently{" "}
                  <span className="text-[#D4B483]">top-right</span>.
                </p>
              </Panel>
            )}

          {summary.trouve_brand_funnel && (
            <Panel
              title="Trouvé brand funnel · homepage → demo → signup"
              meta="Distinct users · Tasks 1-7"
              testid="analytics-trouve-brand-funnel"
            >
              <p className="text-sm text-white/55 mb-5 -mt-3">
                Full Trouvé character journey: <code className="bg-white/8 px-1.5 py-0.5 rounded text-[11px] text-[#D4B483]">trouve_intro_viewed</code> on the homepage
                "Meet Trouvé" section → <code className="bg-white/8 px-1.5 py-0.5 rounded text-[11px] text-[#D4B483]">trouve_demo_started/completed</code> in the demo →
                <code className="bg-white/8 px-1.5 py-0.5 rounded text-[11px] text-[#D4B483]"> trouve_demo_to_signup</code> at the CTA.
                Stealth users emit nothing here by design.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="bg-white/[0.05] rounded-xl border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
                    Intro viewed
                  </div>
                  <div className="text-3xl font-light text-white tabular-nums">
                    {fmt(summary.trouve_brand_funnel.funnel_steps[0]?.users || 0)}
                  </div>
                </div>
                <div className="bg-white/[0.05] rounded-xl border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
                    Intro → Demo
                  </div>
                  <div className="text-3xl font-light text-white tabular-nums">
                    {summary.trouve_brand_funnel.intro_to_demo_pct != null
                      ? `${summary.trouve_brand_funnel.intro_to_demo_pct}%`
                      : "—"}
                  </div>
                </div>
                <div className="bg-white/[0.05] rounded-xl border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
                    Demo → Signup CTA
                  </div>
                  <div className="text-3xl font-light text-white tabular-nums">
                    {summary.trouve_brand_funnel.demo_to_signup_pct != null
                      ? `${summary.trouve_brand_funnel.demo_to_signup_pct}%`
                      : "—"}
                  </div>
                </div>
                <div className="bg-[#D4B483]/10 rounded-xl border border-[#D4B483]/30 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
                    End-to-end
                  </div>
                  <div className="text-3xl font-light text-[#D4B483] tabular-nums">
                    {summary.trouve_brand_funnel.end_to_end_pct != null
                      ? `${summary.trouve_brand_funnel.end_to_end_pct}%`
                      : "—"}
                  </div>
                  <div className="text-[11px] text-white/55 mt-0.5">
                    of intro-viewers signed up
                  </div>
                </div>
              </div>

              <div className="space-y-4 mb-6">
                {summary.trouve_brand_funnel.funnel_steps.map((step, i) => {
                  const peak = summary.trouve_brand_funnel.funnel_steps[0]?.users || 1;
                  return (
                    <FunnelRow
                      key={step.event}
                      label={step.label}
                      users={step.users}
                      peak={peak || 1}
                      color={["#D4B483", "#C68F92", "#9B6A6C", "#5C4B51"][i] || "#D4B483"}
                      dropoffPct={
                        i > 0 && summary.trouve_brand_funnel.funnel_steps[i - 1].users > 0
                          ? Math.round(
                              1000 *
                                (1 -
                                  step.users /
                                    summary.trouve_brand_funnel.funnel_steps[i - 1]
                                      .users),
                            ) / 10
                          : null
                      }
                    />
                  );
                })}
              </div>

              {summary.trouve_brand_funnel.side_events?.length > 0 && (
                <div className="pt-4 border-t border-white/8">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-white/45 mb-3">
                    Side signals · engagement once they're inside
                  </div>
                  <div className="grid grid-cols-3 gap-3">
                    {summary.trouve_brand_funnel.side_events.map((ev) => (
                      <div
                        key={ev.event}
                        className="bg-white/[0.03] rounded-lg border border-white/8 p-3"
                        data-testid={`analytics-trouve-side-${ev.event}`}
                      >
                        <div className="text-[10px] text-white/55 truncate">
                          {ev.label}
                        </div>
                        <div className="text-xl font-light text-white tabular-nums mt-1">
                          {fmt(ev.users || 0)}
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {(summary.trouve_brand_funnel.funnel_steps[0]?.users || 0) === 0 && (
                <div className="text-center py-8 text-sm text-white/50">
                  No Trouvé brand views yet for this window — fresh after this redeploy.
                </div>
              )}
            </Panel>
          )}

          {summary.sample_routine_funnel && (
            <Panel
              title="Sample routine · public marketing funnel"
              meta="Distinct users · /sample-routine"
              testid="analytics-sample-routine-funnel"
            >
              <p className="text-sm text-white/55 mb-5 -mt-3">
                The no-auth, no-selfie teaser at <code className="bg-white/8 px-1.5 py-0.5 rounded text-[11px] text-[#D4B483]">/sample-routine</code> —
                linked from <code className="text-[#D4B483] text-[11px]">/</code> and <code className="text-[#D4B483] text-[11px]">/start</code>.
                Tells you whether cold visitors who preview the product
                actually convert into signups.
                Stealth Mode users emit zero events here by design.
              </p>

              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-6">
                <div className="bg-white/[0.05] rounded-xl border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
                    Opened
                  </div>
                  <div className="text-3xl font-light text-white tabular-nums">
                    {fmt(summary.sample_routine_funnel.steps[0]?.users || 0)}
                  </div>
                </div>
                <div className="bg-white/[0.05] rounded-xl border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
                    Completion
                  </div>
                  <div className="text-3xl font-light text-white tabular-nums">
                    {summary.sample_routine_funnel.completion_pct != null
                      ? `${summary.sample_routine_funnel.completion_pct}%`
                      : "—"}
                  </div>
                  <div className="text-[11px] text-white/40 mt-0.5">
                    completed / opened
                  </div>
                </div>
                <div className="bg-white/[0.05] rounded-xl border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
                    CTA click-through
                  </div>
                  <div className="text-3xl font-light text-white tabular-nums">
                    {summary.sample_routine_funnel.cta_click_pct != null
                      ? `${summary.sample_routine_funnel.cta_click_pct}%`
                      : "—"}
                  </div>
                  <div className="text-[11px] text-white/40 mt-0.5">
                    of completers
                  </div>
                </div>
                <div className="bg-[#D4B483]/10 rounded-xl border border-[#D4B483]/30 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
                    Intent capture
                  </div>
                  <div className="text-3xl font-light text-[#D4B483] tabular-nums">
                    {summary.sample_routine_funnel.intent_capture_pct != null
                      ? `${summary.sample_routine_funnel.intent_capture_pct}%`
                      : "—"}
                  </div>
                  <div className="text-[11px] text-white/55 mt-0.5">
                    of openers signed up
                  </div>
                </div>
              </div>

              <div className="space-y-4">
                {summary.sample_routine_funnel.steps.map((step, i) => {
                  const opened = summary.sample_routine_funnel.steps[0]?.users || 0;
                  return (
                    <FunnelRow
                      key={step.event}
                      label={step.label}
                      users={step.users}
                      peak={opened || 1}
                      color={["#D4B483", "#9B6A6C", "#5C4B51"][i] || "#D4B483"}
                      dropoffPct={
                        i > 0 && summary.sample_routine_funnel.steps[i - 1].users > 0
                          ? Math.round(
                              1000 *
                                (1 -
                                  step.users /
                                    summary.sample_routine_funnel.steps[i - 1]
                                      .users),
                            ) / 10
                          : null
                      }
                    />
                  );
                })}
              </div>
              {(summary.sample_routine_funnel.steps[0]?.users || 0) === 0 && (
                <div className="text-center py-8 text-sm text-white/50">
                  No sample-routine views yet for this window.
                </div>
              )}
            </Panel>
          )}

          {summary.stealth_cohort && (
            <Panel
              title="Stealth Mode · signals"
              meta="Excluded from funnels above"
              testid="analytics-stealth-signals"
            >
              <p className="text-sm text-white/55 mb-5 -mt-3">
                The brand promise on pitch deck slide 10. Stealth users
                emit zero events to <code className="bg-white/8 px-1.5 py-0.5 rounded text-[11px] text-[#D4B483]">analytics_events</code>,
                so we read directly from <code className="bg-white/8 px-1.5 py-0.5 rounded text-[11px] text-[#D4B483]">routines</code>
                (only the stealth flag, not the content).
                These numbers are lifetime, not windowed.
              </p>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-3">
                <div className="bg-white/[0.05] rounded-xl border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
                    Stealth routines · lifetime
                  </div>
                  <div className="text-3xl font-light text-white tabular-nums">
                    {fmt(summary.stealth_cohort.total_stealth_routines_lifetime || 0)}
                  </div>
                </div>
                <div className="bg-white/[0.05] rounded-xl border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
                    Unique stealth users
                  </div>
                  <div className="text-3xl font-light text-white tabular-nums">
                    {fmt(summary.stealth_cohort.unique_stealth_users || 0)}
                  </div>
                </div>
                <div className="bg-white/[0.05] rounded-xl border border-white/10 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
                    Multi-routine stealth
                  </div>
                  <div className="text-3xl font-light text-white tabular-nums">
                    {fmt(summary.stealth_cohort.multi_routine_stealth_users || 0)}
                  </div>
                  <div className="text-[11px] text-white/40 mt-0.5">
                    came back & used stealth again
                  </div>
                </div>
                <div className="bg-[#D4B483]/10 rounded-xl border border-[#D4B483]/30 p-4">
                  <div className="text-[10px] uppercase tracking-[0.22em] text-[#D4B483] mb-1.5">
                    Multi-month stealth
                  </div>
                  <div className="text-3xl font-light text-[#D4B483] tabular-nums">
                    {fmt(summary.stealth_cohort.multi_month_stealth_users || 0)}
                  </div>
                  <div className="text-[11px] text-white/55 mt-0.5">
                    strongest retention signal
                  </div>
                </div>
              </div>
              <div className="text-xs text-white/45 mt-3">
                Avg routines per stealth user:{" "}
                <span className="text-white/75 tabular-nums">
                  {summary.stealth_cohort.avg_stealth_routines_per_user || 0}
                </span>{" "}
                · Median:{" "}
                <span className="text-white/75 tabular-nums">
                  {summary.stealth_cohort.median_stealth_routines_per_user || 0}
                </span>{" "}
                · Power user max:{" "}
                <span className="text-white/75 tabular-nums">
                  {summary.stealth_cohort.max_stealth_routines_one_user || 0}
                </span>
              </div>
            </Panel>
          )}

          <Panel title="All events">
            {summary.counts.length === 0 ? (
              <div className="text-sm text-white/50 py-6 text-center">
                No events yet.
              </div>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {summary.counts.map((c) => (
                  <div
                    key={c.event}
                    className="flex items-baseline justify-between gap-3 py-2 px-3 rounded-xl border border-white/5 hover:bg-white/[0.05] transition-colors"
                    data-testid={`analytics-event-${c.event}`}
                  >
                    <code className="text-sm text-[#D4B483]">{c.event}</code>
                    <span className="text-white tabular-nums">
                      {fmt(c.count)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </Panel>

          {summary.cohorts.length > 0 && (
            <Panel title="By cohort">
              <div className="overflow-x-auto -mx-2 px-2">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="text-left text-[10px] uppercase tracking-[0.22em] text-white/40">
                      <th className="py-2 pr-4">Event</th>
                      <th className="py-2 pr-4">Cohort</th>
                      <th className="py-2 text-right">Count</th>
                    </tr>
                  </thead>
                  <tbody>
                    {summary.cohorts
                      .filter((c) => c.count >= 1)
                      .sort((a, b) => b.count - a.count)
                      .slice(0, 30)
                      .map((c) => (
                        <tr
                          key={`${c.event}-${
                            c.is_pro ? "pro" : c.is_guest ? "guest" : "free"
                          }`}
                          className="border-t border-white/5"
                        >
                          <td className="py-2 pr-4">
                            <code className="text-[#D4B483]">{c.event}</code>
                          </td>
                          <td className="py-2 pr-4 text-white/70">
                            {c.is_pro
                              ? "Pro"
                              : c.is_guest
                                ? "Guest"
                                : "Free / signed-in"}
                          </td>
                          <td className="py-2 text-right tabular-nums text-white">
                            {fmt(c.count)}
                          </td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </Panel>
          )}

          <div className="h-6 flex items-center justify-center">
            <BarChart3 className="h-4 w-4 text-white/20" />
          </div>
        </>
      )}
    </AdminShell>
  );
}
